import React, { useState, useEffect, useRef } from 'react';
import { Clipboard, Check, AlertCircle, Loader, FileText, X, CheckCircle } from 'lucide-react';
import { useTemplates, useReservationContext, templateApi } from '../../hooks/useTemplates';
import './AdminPanel.css';

function ApprovalEmailModal({ reservation, onClose, onApprove }) {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [rendered, setRendered] = useState(null);
  const [renderError, setRenderError] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const iframeRef = useRef(null);

  const { templates, isLoading: templatesLoading } = useTemplates();
  const { context, isLoading: contextLoading, error: contextError } = useReservationContext(
    reservation ? String(reservation.id) : null
  );

  // Auto-select first Confirmation template when templates load
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      const confirmTemplate = templates.find(
        (t) => t.category === 'Confirmation' && t.is_active
      );
      setSelectedTemplateId(confirmTemplate ? String(confirmTemplate.id) : String(templates[0].id));
    }
  }, [templates, selectedTemplateId]);

  const handleRender = async () => {
    if (!selectedTemplateId || !context) return;
    setIsRendering(true);
    setRenderError(null);
    setRendered(null);
    try {
      const result = await templateApi.preview(selectedTemplateId, context);
      setRendered(result.data);
    } catch (err) {
      setRenderError(err.response?.data?.error || err.message || 'Rendering failed');
    } finally {
      setIsRendering(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const copyRichText = async () => {
    if (!rendered?.htmlBody) return;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rendered.htmlBody, 'text/html');
      const bodyHtml = doc.body.innerHTML;
      const blob = new Blob([bodyHtml], { type: 'text/html' });
      const item = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([item]);
      setCopiedField('rich');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      try {
        const iframe = iframeRef.current;
        const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
        if (iframeDoc) {
          const range = iframeDoc.createRange();
          range.selectNodeContents(iframeDoc.body);
          const sel = iframeDoc.defaultView.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          iframeDoc.execCommand('copy');
          sel.removeAllRanges();
        }
        setCopiedField('rich');
        setTimeout(() => setCopiedField(null), 2000);
      } catch {
        copyToClipboard(rendered.htmlBody, 'rich');
      }
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await onApprove(reservation.id);
    } finally {
      setIsApproving(false);
    }
  };

  const selectedTemplate = templates.find((t) => String(t.id) === selectedTemplateId);
  const canRender = selectedTemplateId && context && !contextLoading;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="approval-modal-overlay" onClick={onClose}>
      <div className="approval-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="approval-modal-header">
          <div>
            <h3 className="approval-modal-title">Approve Reservation</h3>
            <p className="approval-modal-subtitle">
              {reservation.user_name || reservation.email} &mdash;&nbsp;
              {formatDate(reservation.check_in_date)} &ndash; {formatDate(reservation.check_out_date)}
            </p>
          </div>
          <button className="approval-modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="approval-modal-body">
          {/* Template selector */}
          <div className="approval-modal-section">
            <label className="approval-modal-label">Confirmation Email Template</label>
            {templatesLoading ? (
              <div className="approval-modal-loading">
                <Loader size={14} className="template-preview-spinner" /> Loading templates...
              </div>
            ) : templates.length === 0 ? (
              <p className="approval-modal-hint">
                No templates found. Create one in the Email tab first.
              </p>
            ) : (
              <div className="approval-modal-template-row">
                <select
                  className="approval-modal-select"
                  value={selectedTemplateId}
                  onChange={(e) => {
                    setSelectedTemplateId(e.target.value);
                    setRendered(null);
                    setRenderError(null);
                  }}
                >
                  <option value="">Select a template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.category}){!t.is_active ? ' [inactive]' : ''}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-primary"
                  onClick={handleRender}
                  disabled={!canRender || isRendering}
                >
                  {isRendering ? (
                    <><Loader size={14} className="template-preview-spinner" /> Rendering...</>
                  ) : (
                    <><FileText size={14} /> Preview</>
                  )}
                </button>
              </div>
            )}
            {selectedTemplate && (
              <p className="approval-modal-hint">Subject: {selectedTemplate.subject_tpl}</p>
            )}
            {contextLoading && (
              <div className="approval-modal-loading">
                <Loader size={14} className="template-preview-spinner" /> Loading reservation data...
              </div>
            )}
            {contextError && (
              <div className="approval-modal-error">
                <AlertCircle size={14} /> Could not load reservation context
              </div>
            )}
          </div>

          {/* Render error */}
          {renderError && (
            <div className="approval-modal-error">
              <AlertCircle size={14} /> {renderError}
            </div>
          )}

          {/* Rendered output */}
          {rendered && (
            <div className="approval-modal-output">
              {/* Subject */}
              <div className="approval-modal-output-row">
                <div className="approval-modal-output-label">
                  <span>Subject</span>
                  <button
                    className="quick-render-copy-btn"
                    onClick={() => copyToClipboard(rendered.subject, 'subject')}
                  >
                    {copiedField === 'subject' ? <><Check size={13} /> Copied</> : <><Clipboard size={13} /> Copy</>}
                  </button>
                </div>
                <div className="approval-modal-subject">{rendered.subject}</div>
              </div>

              {/* Body */}
              <div className="approval-modal-output-row">
                <div className="approval-modal-output-label">
                  <span>Email Body</span>
                  <div className="quick-render-copy-btns">
                    <button className="quick-render-copy-btn" onClick={copyRichText}>
                      {copiedField === 'rich' ? <><Check size={13} /> Copied</> : <><Clipboard size={13} /> Copy for Outlook</>}
                    </button>
                    <button className="quick-render-copy-btn" onClick={() => copyToClipboard(rendered.htmlBody, 'body')}>
                      {copiedField === 'body' ? <><Check size={13} /> Copied</> : <><Clipboard size={13} /> Copy HTML</>}
                    </button>
                  </div>
                </div>
                <div className="approval-modal-iframe-wrap">
                  <iframe
                    ref={iframeRef}
                    title="Email Preview"
                    srcDoc={rendered.htmlBody}
                    sandbox=""
                    className="approval-modal-iframe"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="approval-modal-footer">
          <button className="btn btn-neutral" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={handleApprove}
            disabled={isApproving}
          >
            {isApproving ? (
              <><Loader size={14} className="template-preview-spinner" /> Approving...</>
            ) : (
              <><CheckCircle size={14} /> Approve Reservation</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ApprovalEmailModal;
